import {CreateAccessor} from "mobx-graphlink";
import {GetEntity} from "../../../../Server/DB/entities.js";
import {GetJournalEntries} from "../../../../Server/DB/journalEntries.js";
import {GetScenesValidToBeLetterAnchors, GetStandardDeepOrderingKeyForDreamScene} from "../../../../Server/DB/journalEntries/$journalEntry.js";
import {MeID} from "../../../../Server/DB/users.js";

/**
 * A single grid-scene prompt candidate, tagged with its grid-cell (anchor-letter)
 * and its "generation distance" (how many scenes newer than it exist in its cell).
 */
export type GridSceneCandidate = {
	text: string; // the sceneTitle, verbatim (spoken by TTS)
	letter: string; // the A-Z grid-cell this scene belongs to (first letter of its anchor-entity's name)
	entityName: string; // the anchor-entity's name, verbatim
	generation: number; // 0 = latest scene for that cell; higher = older
};

/** Get all grid-scene candidates for the current user's journal. Only scenes that have an anchor entity
 * assigned are included, grouped by the anchor-letter (grid-cell). Within each cell, scenes are ordered
 * newest-first, and each is tagged with its generation-distance (0 = the latest scene for that cell). */
export const GetGridScenesData = CreateAccessor((): GridSceneCandidate[]=>{
	const entries = GetJournalEntries(MeID());
	const validScenes = GetScenesValidToBeLetterAnchors(entries)
		.filter(scene=>scene.sceneAnchorId != null);
	// order newest-first (matching the A-Z recall-grid "latest generation" semantics)
	const validScenes_newestFirst = validScenes.OrderByDescending(scene=>GetStandardDeepOrderingKeyForDreamScene(scene));

	// group by grid-cell letter, tracking per-cell generation-distance
	const result: GridSceneCandidate[] = [];
	const nextGenDistanceByLetter = new Map<string, number>();
	for (const scene of validScenes_newestFirst) {
		const anchorEntity = scene.sceneAnchorId ? GetEntity(scene.sceneAnchorId) : null;
		const entityName = anchorEntity?.name ?? null;
		const letter = entityName?.[0]?.toLowerCase() ?? null;
		if (letter == null || entityName == null) continue; // skip if anchor entity can't be resolved to a letter/name

		const genDistance = nextGenDistanceByLetter.get(letter) ?? 0;
		nextGenDistanceByLetter.set(letter, genDistance + 1);

		result.push({text: scene.sceneTitle, letter, entityName, generation: genDistance});
	}
	return result;
});
